import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { formatApiError } from "@/lib/api";
import { formatRelativeTimeIST } from "@/lib/time";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  Package,
  Barcode,
  IndianRupee,
  AlertTriangle,
  Info,
  History,
  ShoppingCart,
  RotateCcw,
  TruckIcon,
  Layers,
  Store,
  Calendar,
  User,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  ExternalLink,
  Loader2,
} from "lucide-react";

// Format relative time using shared IST calendar-day utility
function formatRelativeTime(dateStr) {
  return formatRelativeTimeIST(dateStr);
}

// Format date
function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

// Warning badge component
function WarningBadge({ warning }) {
  const severityColors = {
    high: "bg-red-100 text-red-800 border-red-200",
    medium: "bg-orange-100 text-orange-800 border-orange-200",
    low: "bg-yellow-100 text-yellow-800 border-yellow-200",
  };
  
  return (
    <div className={`flex items-start gap-2 p-3 rounded-lg border ${severityColors[warning.severity] || severityColors.medium}`}>
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="text-sm">
        <div className="font-medium">{warning.message}</div>
        {warning.other_products && warning.other_products.length > 0 && (
          <div className="mt-1 text-xs">
            Other products: {warning.other_products.map(p => p.product).join(", ")}
          </div>
        )}
      </div>
    </div>
  );
}

// Stock summary section
function StockSummary({ stock }) {
  if (!stock) return null;
  
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Layers className="w-4 h-4" />
          Stock Overview
        </CardTitle>
        <CardDescription>Current inventory levels (read-only)</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <div className="text-2xl font-bold text-slate-900">{stock.total_quantity}</div>
            <div className="text-xs text-muted-foreground">Total Quantity</div>
          </div>
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <div className="text-2xl font-bold text-slate-900">{stock.batch_count}</div>
            <div className="text-xs text-muted-foreground">Batches</div>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{stock.expired_quantity}</div>
            <div className="text-xs text-muted-foreground">Expired</div>
          </div>
          <div className="text-center p-3 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">{stock.expiring_soon_quantity}</div>
            <div className="text-xs text-muted-foreground">Expiring Soon</div>
          </div>
        </div>
        
        {stock.shops && stock.shops.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">By Shop</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {stock.shops.map((shop) => (
                <div 
                  key={shop.shop_id} 
                  className="flex items-center justify-between p-2 bg-slate-50 rounded border"
                >
                  <div className="flex items-center gap-2">
                    <Store className="w-3 h-3 text-muted-foreground" />
                    <span className="text-sm font-medium">{shop.shop_name}</span>
                  </div>
                  <Badge variant="outline" className="font-mono">
                    {shop.quantity} units
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Activity section
function ActivitySection({ activity }) {
  if (!activity) return null;
  
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <History className="w-4 h-4" />
          Product Activity
        </CardTitle>
        <CardDescription>Recent transactions and movements</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{activity.sales_count}</div>
            <div className="text-xs text-muted-foreground">Total Sales</div>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">{activity.return_count}</div>
            <div className="text-xs text-muted-foreground">Returns</div>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{activity.purchase_order_count}</div>
            <div className="text-xs text-muted-foreground">Purchase Orders</div>
          </div>
        </div>
        
        <Tabs defaultValue="sales" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="sales" className="text-xs">
              <ShoppingCart className="w-3 h-3 mr-1" />
              Sales
            </TabsTrigger>
            <TabsTrigger value="returns" className="text-xs">
              <RotateCcw className="w-3 h-3 mr-1" />
              Returns
            </TabsTrigger>
            <TabsTrigger value="movements" className="text-xs">
              <TruckIcon className="w-3 h-3 mr-1" />
              Movements
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="sales" className="mt-3">
            {activity.recent_sales && activity.recent_sales.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {activity.recent_sales.map((sale, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                    <div>
                      <div className="font-medium">{sale.sale_number || sale.sale_id?.slice(0, 8)}</div>
                      <div className="text-xs text-muted-foreground">
                        {sale.customer_name || "Walk-in"} • {sale.shop_name}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono">×{sale.quantity} @ ₹{sale.unit_price || sale.mrp}</div>
                      <div className="text-xs text-muted-foreground">{formatRelativeTime(sale.date)}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground text-sm">No recent sales</div>
            )}
          </TabsContent>
          
          <TabsContent value="returns" className="mt-3">
            {activity.recent_returns && activity.recent_returns.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {activity.recent_returns.map((ret, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                    <div>
                      <div className="font-medium">{ret.return_number || ret.return_id?.slice(0, 8)}</div>
                      <div className="text-xs text-muted-foreground">{ret.shop_name}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono">×{ret.quantity}</div>
                      <div className="text-xs text-muted-foreground">{formatRelativeTime(ret.date)}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground text-sm">No returns</div>
            )}
          </TabsContent>
          
          <TabsContent value="movements" className="mt-3">
            {activity.stock_movements && activity.stock_movements.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {activity.stock_movements.map((mov, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                    <div>
                      <Badge variant="outline" className="text-xs">{mov.type}</Badge>
                      <div className="text-xs text-muted-foreground mt-1">{mov.shop_name}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono">{mov.quantity > 0 ? "+" : ""}{mov.quantity}</div>
                      <div className="text-xs text-muted-foreground">{formatRelativeTime(mov.date)}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground text-sm">No stock movements</div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

// Review warnings section
function ReviewWarningsSection({ reviews, productId }) {
  if (!reviews || reviews.length === 0) return null;
  
  const statusColors = {
    new: "bg-blue-100 text-blue-800",
    under_review: "bg-yellow-100 text-yellow-800",
    reviewed: "bg-green-100 text-green-800",
    dismissed: "bg-gray-100 text-gray-600",
  };
  
  const typeLabels = {
    pp_greater_than_mrp: "PP > MRP",
    potential_duplicate: "Potential Duplicate",
    mrp_zero_or_missing: "MRP Zero/Missing",
    shared_manual_barcode: "Shared Barcode",
  };
  
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-orange-500" />
          Data Review Items
        </CardTitle>
        <CardDescription>Product data review notifications</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {reviews.map((review) => (
            <Link
              key={review.id}
              to={`/product-data-review?item=${review.id}`}
              className="block p-3 bg-slate-50 rounded-lg border hover:bg-slate-100 transition-colors"
              data-testid={`review-item-${review.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {typeLabels[review.issue_type] || review.issue_type}
                    </Badge>
                    <Badge className={`text-xs ${statusColors[review.status]}`}>
                      {review.status}
                    </Badge>
                  </div>
                  <div className="text-sm mt-1">{review.reason}</div>
                </div>
                <ExternalLink className="w-4 h-4 text-muted-foreground shrink-0" />
              </div>
              {review.notes && (
                <div className="mt-2 text-xs text-muted-foreground italic">
                  Notes: {review.notes}
                </div>
              )}
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Audit history section
function AuditHistorySection({ history }) {
  if (!history || history.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Edit History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-muted-foreground text-sm">
            No edit history available
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Edit History
        </CardTitle>
        <CardDescription>Product change audit trail</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {history.map((event, idx) => (
            <div key={idx} className="p-3 bg-slate-50 rounded-lg border">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{event.action}</Badge>
                    <span className="text-xs text-muted-foreground">{event.module}</span>
                  </div>
                  {event.summary && (
                    <div className="text-sm mt-1">{event.summary}</div>
                  )}
                  {event.field_changes && event.field_changes.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {event.field_changes.map((change, cidx) => (
                        <div key={cidx} className="text-xs font-mono bg-white p-1 rounded">
                          <span className="text-muted-foreground">{change.field}:</span>{" "}
                          <span className="text-red-600 line-through">{String(change.old_value ?? "—")}</span>{" "}
                          <span className="text-green-600">{String(change.new_value ?? "—")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                <User className="w-3 h-3" />
                <span>{event.actor_name || event.actor_phone || "System"}</span>
                <span>•</span>
                <Clock className="w-3 h-3" />
                <span>{formatDate(event.timestamp)}</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ProductProfile() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [error, setError] = useState(null);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editWarnings, setEditWarnings] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  
  // Fetch product profile
  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/products/${productId}/profile`);
      setProfileData(res.data);
      // Initialize edit form with current values
      if (res.data.product) {
        setEditForm({
          product: res.data.product.product || "",
          brand_name: res.data.product.brand_name || "",
          group_name: res.data.product.group_name || "",
          manual_barcode: res.data.product.manual_barcode || "",
          mrp: res.data.product.mrp || 0,
          purchase_price: res.data.product.purchase_price || 0,
          last_purchase_cost: res.data.product.last_purchase_cost || 0,
          default_price: res.data.product.default_price || 0,
        });
      }
    } catch (err) {
      console.error("Failed to fetch product profile:", err);
      if (err.response?.status === 404) {
        setError("Product not found");
      } else if (err.response?.status === 403) {
        setError("You don't have permission to view this product");
      } else {
        setError(formatApiError(err) || "Failed to load product profile");
      }
    } finally {
      setLoading(false);
    }
  }, [productId]);
  
  useEffect(() => {
    if (productId) {
      fetchProfile();
    }
  }, [productId, fetchProfile]);
  
  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setEditForm((prev) => {
      const newForm = { ...prev, [field]: value };
      
      // Recompute warnings based on the full form
      const newWarnings = [];
      const mrp = parseFloat(newForm.mrp) || 0;
      const pp = parseFloat(newForm.purchase_price) || 0;
      
      // PP > MRP warning
      if (mrp > 0 && pp > mrp) {
        newWarnings.push({
          type: "pp_greater_than_mrp",
          message: `Purchase price (₹${pp}) exceeds MRP (₹${mrp})`,
        });
      }
      
      // MRP = 0 warning
      if (mrp === 0 || !newForm.mrp) {
        newWarnings.push({
          type: "mrp_zero",
          message: "MRP is zero or missing",
        });
      }
      
      setEditWarnings(newWarnings);
      return newForm;
    });
  };
  
  // Enter edit mode
  const startEditing = () => {
    setIsEditing(true);
    setEditWarnings([]);
    if (profileData?.product) {
      setEditForm({
        product: profileData.product.product || "",
        brand_name: profileData.product.brand_name || "",
        group_name: profileData.product.group_name || "",
        manual_barcode: profileData.product.manual_barcode || "",
        mrp: profileData.product.mrp || 0,
        purchase_price: profileData.product.purchase_price || 0,
        last_purchase_cost: profileData.product.last_purchase_cost || 0,
        default_price: profileData.product.default_price || 0,
      });
    }
  };
  
  // Cancel editing
  const cancelEditing = () => {
    setIsEditing(false);
    setEditWarnings([]);
    if (profileData?.product) {
      setEditForm({
        product: profileData.product.product || "",
        brand_name: profileData.product.brand_name || "",
        group_name: profileData.product.group_name || "",
        manual_barcode: profileData.product.manual_barcode || "",
        mrp: profileData.product.mrp || 0,
        purchase_price: profileData.product.purchase_price || 0,
        last_purchase_cost: profileData.product.last_purchase_cost || 0,
        default_price: profileData.product.default_price || 0,
      });
    }
  };
  
  // Compute changes and show confirmation
  const prepareChanges = () => {
    const changes = [];
    const product = profileData?.product || {};
    
    const fields = [
      { key: "product", label: "Product Name" },
      { key: "brand_name", label: "Brand" },
      { key: "group_name", label: "Group" },
      { key: "manual_barcode", label: "Manual Barcode" },
      { key: "mrp", label: "MRP", isNumeric: true },
      { key: "purchase_price", label: "Purchase Price", isNumeric: true },
      { key: "last_purchase_cost", label: "Last Purchase Cost", isNumeric: true },
      { key: "default_price", label: "Default Price", isNumeric: true },
    ];
    
    for (const field of fields) {
      const oldVal = product[field.key];
      const newVal = editForm[field.key];
      
      if (field.isNumeric) {
        const oldNum = parseFloat(oldVal) || 0;
        const newNum = parseFloat(newVal) || 0;
        if (Math.abs(oldNum - newNum) >= 0.01) {
          changes.push({
            field: field.key,
            label: field.label,
            oldValue: `₹${oldNum.toFixed(2)}`,
            newValue: `₹${newNum.toFixed(2)}`,
          });
        }
      } else {
        const oldStr = String(oldVal || "").trim();
        const newStr = String(newVal || "").trim();
        if (oldStr !== newStr) {
          changes.push({
            field: field.key,
            label: field.label,
            oldValue: oldStr || "(empty)",
            newValue: newStr || "(empty)",
          });
        }
      }
    }
    
    if (changes.length === 0) {
      toast.info("No changes to save");
      return;
    }
    
    setPendingChanges(changes);
    setConfirmDialogOpen(true);
  };
  
  // Save changes
  const saveChanges = async () => {
    try {
      setSaving(true);
      setConfirmDialogOpen(false);
      
      // Build update payload with only changed fields
      const updatePayload = {};
      const product = profileData?.product || {};
      
      const numericFields = ["mrp", "purchase_price", "last_purchase_cost", "default_price"];
      const stringFields = ["product", "brand_name", "group_name", "manual_barcode"];
      
      for (const field of stringFields) {
        const oldVal = String(product[field] || "").trim();
        const newVal = String(editForm[field] || "").trim();
        if (oldVal !== newVal) {
          updatePayload[field] = newVal;
        }
      }
      
      for (const field of numericFields) {
        const oldVal = parseFloat(product[field]) || 0;
        const newVal = parseFloat(editForm[field]) || 0;
        if (Math.abs(oldVal - newVal) >= 0.01) {
          updatePayload[field] = newVal;
        }
      }
      
      if (Object.keys(updatePayload).length === 0) {
        toast.info("No changes to save");
        setSaving(false);
        return;
      }
      
      const res = await api.patch(`/products/${productId}`, updatePayload);
      
      if (res.data.success) {
        toast.success(res.data.message || "Product updated successfully");
        
        // Show warnings if any
        if (res.data.warnings && res.data.warnings.length > 0) {
          for (const warning of res.data.warnings) {
            toast.warning(warning.message, { duration: 5000 });
          }
        }
        
        setIsEditing(false);
        // Refresh the profile
        await fetchProfile();
      } else {
        toast.error("Update failed");
        // Re-fetch on failure to sync UI with DB state
        await fetchProfile();
      }
    } catch (err) {
      console.error("Failed to update product:", err);
      if (err.response?.status === 403) {
        toast.error("You don't have permission to edit this product");
      } else {
        toast.error(formatApiError(err) || "Failed to update product");
      }
      // Re-fetch on error to sync UI with DB state (server may have partially updated)
      await fetchProfile();
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };
  
  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="product-profile-loading">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8" data-testid="product-profile-error">
        <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Error</h2>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={() => navigate("/inventory")} data-testid="back-to-inventory-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Inventory
        </Button>
      </div>
    );
  }
  
  if (!profileData || !profileData.product) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8">
        <AlertTriangle className="w-12 h-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Product Not Found</h2>
        <p className="text-muted-foreground mb-4">The requested product could not be found.</p>
        <Button onClick={() => navigate("/inventory")} data-testid="back-to-inventory-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Inventory
        </Button>
      </div>
    );
  }
  
  const { product, stock, activity, reviews, warnings, shared_barcode_products, audit_history, can_edit } = profileData;
  
  return (
    <div className="container mx-auto py-4 px-4 max-w-5xl" data-testid="product-profile-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/inventory")}
            className="mb-2 -ml-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Inventory
          </Button>
          <h1 className="text-2xl font-bold" data-testid="product-name">
            {isEditing ? (
              <Input
                value={editForm.product}
                onChange={(e) => handleFieldChange("product", e.target.value)}
                className="text-2xl font-bold h-auto py-1 px-2"
                data-testid="edit-product-name"
              />
            ) : (
              product.product
            )}
          </h1>
          <div className="flex items-center gap-2 mt-1 text-muted-foreground">
            {isEditing ? (
              <>
                <Input
                  value={editForm.brand_name}
                  onChange={(e) => handleFieldChange("brand_name", e.target.value)}
                  placeholder="Brand"
                  className="h-7 text-sm w-32"
                  data-testid="edit-brand-name"
                />
                <span>/</span>
                <Input
                  value={editForm.group_name}
                  onChange={(e) => handleFieldChange("group_name", e.target.value)}
                  placeholder="Group"
                  className="h-7 text-sm w-32"
                  data-testid="edit-group-name"
                />
              </>
            ) : (
              <>
                <span>{product.brand_name || "—"}</span>
                <span>/</span>
                <span>{product.group_name || "—"}</span>
              </>
            )}
          </div>
        </div>
        
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button
                variant="outline"
                onClick={cancelEditing}
                disabled={saving}
                data-testid="cancel-edit-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button
                onClick={prepareChanges}
                disabled={saving}
                data-testid="save-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-1" />
                )}
                Save Changes
              </Button>
            </>
          ) : (
            can_edit && (
              <Button
                onClick={startEditing}
                data-testid="edit-btn"
              >
                <Edit className="w-4 h-4 mr-1" />
                Edit Product
              </Button>
            )
          )}
        </div>
      </div>
      
      {/* Edit warnings */}
      {isEditing && editWarnings.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="edit-warnings">
          {editWarnings.map((warning, idx) => (
            <div 
              key={idx}
              className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg text-orange-800"
            >
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm">{warning.message}</span>
            </div>
          ))}
        </div>
      )}
      
      {/* Warnings from profile data */}
      {!isEditing && warnings && warnings.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="profile-warnings">
          {warnings.map((warning, idx) => (
            <WarningBadge key={idx} warning={warning} />
          ))}
        </div>
      )}
      
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Product Identity */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="w-4 h-4" />
                Product Identity
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Product ID</Label>
                  <div className="font-mono text-sm" data-testid="product-id">{product.id}</div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Auto Barcode (Immutable)</Label>
                  <div className="font-mono text-sm font-medium" data-testid="auto-barcode">
                    {product.auto_barcode}
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Manual Barcode</Label>
                {isEditing ? (
                  <Input
                    value={editForm.manual_barcode}
                    onChange={(e) => handleFieldChange("manual_barcode", e.target.value)}
                    className="font-mono"
                    data-testid="edit-manual-barcode"
                  />
                ) : (
                  <div className="font-mono text-sm" data-testid="manual-barcode">
                    {product.manual_barcode || "—"}
                  </div>
                )}
                {shared_barcode_products && shared_barcode_products.length > 0 && (
                  <div className="mt-1 text-xs text-orange-600">
                    Shared by {shared_barcode_products.length} other product(s)
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Created</Label>
                  <div className="text-sm">{formatDate(product.created_at)}</div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Last Updated</Label>
                  <div className="text-sm">{formatDate(product.updated_at)}</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Pricing */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <IndianRupee className="w-4 h-4" />
                Pricing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">MRP</Label>
                  {isEditing ? (
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editForm.mrp}
                        onChange={(e) => handleFieldChange("mrp", e.target.value)}
                        className="pl-7 font-mono"
                        data-testid="edit-mrp"
                      />
                    </div>
                  ) : (
                    <div className="text-lg font-semibold font-mono" data-testid="mrp">
                      ₹{(product.mrp || 0).toFixed(2)}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Purchase Price</Label>
                  {isEditing ? (
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editForm.purchase_price}
                        onChange={(e) => handleFieldChange("purchase_price", e.target.value)}
                        className="pl-7 font-mono"
                        data-testid="edit-purchase-price"
                      />
                    </div>
                  ) : (
                    <div className="text-lg font-mono" data-testid="purchase-price">
                      ₹{(product.purchase_price || 0).toFixed(2)}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Last Purchase Cost</Label>
                  {isEditing ? (
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editForm.last_purchase_cost}
                        onChange={(e) => handleFieldChange("last_purchase_cost", e.target.value)}
                        className="pl-7 font-mono"
                        data-testid="edit-last-purchase-cost"
                      />
                    </div>
                  ) : (
                    <div className="text-lg font-mono" data-testid="last-purchase-cost">
                      ₹{(product.last_purchase_cost || 0).toFixed(2)}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Default/Selling Price</Label>
                  {isEditing ? (
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editForm.default_price}
                        onChange={(e) => handleFieldChange("default_price", e.target.value)}
                        className="pl-7 font-mono"
                        data-testid="edit-default-price"
                      />
                    </div>
                  ) : (
                    <div className="text-lg font-mono" data-testid="default-price">
                      ₹{(product.default_price || 0).toFixed(2)}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Stock Summary */}
          <StockSummary stock={stock} />
        </div>
        
        {/* Right Column */}
        <div className="space-y-4">
          {/* Activity */}
          <ActivitySection activity={activity} />
          
          {/* Review Warnings */}
          {reviews && reviews.length > 0 && (
            <ReviewWarningsSection reviews={reviews} productId={product.id} />
          )}
          
          {/* Audit History */}
          <AuditHistorySection history={audit_history} />
        </div>
      </div>
      
      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent data-testid="confirm-changes-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to update the following fields. This will NOT affect historical sales or transactions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-2 my-4 max-h-60 overflow-y-auto">
            {pendingChanges.map((change, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                <span className="font-medium text-sm">{change.label}</span>
                <div className="text-sm font-mono">
                  <span className="text-red-600 line-through">{change.oldValue}</span>
                  {" → "}
                  <span className="text-green-600">{change.newValue}</span>
                </div>
              </div>
            ))}
          </div>
          
          {editWarnings.length > 0 && (
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center gap-2 text-orange-800 font-medium text-sm mb-1">
                <AlertTriangle className="w-4 h-4" />
                Warnings
              </div>
              {editWarnings.map((warning, idx) => (
                <div key={idx} className="text-sm text-orange-700">{warning.message}</div>
              ))}
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-confirm-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={saveChanges} data-testid="confirm-save-btn">
              Save Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
